"use client";
/**
 * EmployeeTable.jsx
 * @description Componente para mostrar y gestionar la tabla de empleados.
 * @date 27-10-2025
 * @autor : Joaquin
 */
import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TextField,
  Chip,
  Typography,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Skeleton,
  TablePagination,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  PersonAdd,
  ContentCopy,
} from "@mui/icons-material";
import { useEmpleados } from "../hooks/useEmpleados";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Navbar from "../../../../Navbar";
import EmployeeAvatar from "../../../components/EmployeeAvatar";
import { useDebounce } from "../../../../../Hooks/UseDebounce";

export default function EmployeeTable() {
  // Estados para la paginacion -> Lo hago desde el backend no memoria ;p
  const [searchTerm, setSearchTerm] = useState("");
  const searchDebounced = useDebounce(searchTerm, 500);
  const [page, setPage] = useState(sessionStorage.getItem("page") || 1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const { empleados, deactivateEmployee, loading, getEmpleados } = useEmpleados(
    searchDebounced,
    rowsPerPage,
    page
  );

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    employee: null,
  });

  const navigate = useNavigate();

  const [copiedEmail, setCopiedEmail] = useState(null);

  const handleCopyEmail = (email, cuil) => {
    navigator.clipboard.writeText(email);
    setCopiedEmail(cuil);
    setTimeout(() => setCopiedEmail(null), 2000);
  };

  const handleAddNew = () => {
    navigate("/rrhh2/legajos/formulariov2");
  };

  useEffect(() => {
    if (searchDebounced !== "") {
      setPage(1);
      sessionStorage.setItem("page", 1);
    }
  }, [searchDebounced]);

  const handleEdit = (employee) => {
    const cuil = employee.cuil;
    navigate(`/rrhh2/legajos/formulariov2/`, {
      state: { cuil: cuil, empleado: employee },
    });
  };

  const handleToggleActive = (e, employee) => {
    e.stopPropagation();
    setConfirmDialog({ open: true, employee });
  };

  const handleConfirmDeactivate = () => {
    if (confirmDialog.employee) {
      deactivateEmployee(confirmDialog.employee);
    }
    setConfirmDialog({ open: false, employee: null });
  };

  const handleCancelDeactivate = () => {
    setConfirmDialog({ open: false, employee: null });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
    sessionStorage.setItem("page", newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    sessionStorage.setItem("page", 1);
    setPage(1);
  };

  useEffect(() => {
    getEmpleados(searchDebounced, rowsPerPage, page);
  }, [searchDebounced, rowsPerPage, page]);

  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      <Navbar />
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          p: 2,
          bgcolor: "rgba(21, 101, 192, 0.03)",
          borderRadius: 3,
          border: "1px solid rgba(21, 101, 192, 0.08)",
        }}
      >
        <TextField
          size="small"
          placeholder="Buscar empleado por nombre, CUIL o legajo..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            minWidth: 350,
            "& .MuiOutlinedInput-root": {
              borderRadius: "20px",
              bgcolor: "background.paper",
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchTerm("")}
                  edge="end"
                  sx={{
                    "&:hover": {
                      color: "error.main",
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<PersonAdd />}
          onClick={handleAddNew}
          sx={{
            borderRadius: "20px",
            px: 3,
            textTransform: "none",
            fontWeight: "bold",
            boxShadow: "0 4px 14px 0 rgba(0,118,255,0.39)",
            "&:hover": {
              boxShadow: "0 6px 20px rgba(0,118,255,0.23)",
            },
          }}
        >
          Nuevo Legajo
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 4,
          border: "1px solid rgba(0,0,0,0.05)",
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0,0,0,0.02)",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: "#f8fbfc", fontWeight: "bold" }}>
                Empleado
              </TableCell>
              <TableCell sx={{ bgcolor: "#f8fbfc", fontWeight: "bold" }}>
                Documento / CUIL
              </TableCell>
              <TableCell sx={{ bgcolor: "#f8fbfc", fontWeight: "bold" }}>
                Legajo
              </TableCell>
              <TableCell sx={{ bgcolor: "#f8fbfc", fontWeight: "bold" }}>
                Ingreso
              </TableCell>
              <TableCell sx={{ bgcolor: "#f8fbfc", fontWeight: "bold" }}>
                Estado
              </TableCell>
              {/* <TableCell align="center" sx={{ bgcolor: '#f8fbfc', fontWeight: 'bold' }}>Acciones</TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from(new Array(rowsPerPage)).map((_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={6}>
                    <Skeleton height={50} variant="text" />
                  </TableCell>
                </TableRow>
              ))
            ) : empleados?.data?.length > 0 ? (
              empleados.data.map((emp) => (
                <TableRow
                  key={emp.cuil}
                  hover
                  sx={{
                    cursor: "pointer",
                    "&:last-child td, &:last-child th": { border: 0 },
                    transition: "background-color 0.2s",
                    "&:hover": { bgcolor: "rgba(21, 101, 192, 0.02)" },
                  }}
                  onClick={() => handleEdit(emp)}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <EmployeeAvatar
                        cuil={emp.cuil}
                        nombre={emp.nombre}
                        apellido={emp.apellido}
                        size={38}
                      />
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          {emp.nombre} {emp.apellido}
                        </Typography>
                        <Tooltip
                          title={
                            copiedEmail === emp.cuil
                              ? "¡Copiado!"
                              : "Click para copiar email"
                          }
                          arrow
                          placement="right"
                          open={copiedEmail === emp.cuil ? true : undefined}
                        >
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              if (emp.email) {
                                handleCopyEmail(emp.email, emp.cuil);
                              }
                            }}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                              cursor: emp.email ? "pointer" : "default",
                              transition: "all 0.2s",
                              padding: "2px 6px",
                              borderRadius: 1,
                              bgcolor:
                                copiedEmail === emp.cuil
                                  ? "success.lighter"
                                  : "transparent",
                              "&:hover": emp.email && {
                                bgcolor:
                                  copiedEmail === emp.cuil
                                    ? "success.lighter"
                                    : "action.hover",
                              },
                            }}
                          >
                            <Typography
                              variant="caption"
                              color={
                                copiedEmail === emp.cuil
                                  ? "success.main"
                                  : "text.secondary"
                              }
                              sx={{
                                transition: "color 0.3s",
                                fontWeight:
                                  copiedEmail === emp.cuil ? 600 : 400,
                              }}
                            >
                              {emp.email || "Sin email"}
                            </Typography>
                            {emp.email && (
                              <ContentCopy
                                sx={{
                                  fontSize: 12,
                                  color:
                                    copiedEmail === emp.cuil
                                      ? "success.main"
                                      : "action.disabled",
                                  transition: "all 0.3s",
                                  transform:
                                    copiedEmail === emp.cuil
                                      ? "scale(1.2)"
                                      : "scale(1)",
                                }}
                              />
                            )}
                          </Box>
                        </Tooltip>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{emp.nro_doc}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {emp.cuil}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={emp.nro_legajo}
                      size="small"
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                    />
                  </TableCell>
                  <TableCell>
                    {moment(emp.fecha_ingreso).format("DD/MM/YYYY")}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={emp.activo === 1 ? "Activo" : "Baja"}
                      color={emp.activo === 1 ? "success" : "error"}
                      variant="tonal"
                      size="small"
                      sx={{
                        fontWeight: "bold",
                        minWidth: 70,
                        bgcolor:
                          emp.activo === 1
                            ? "success.lighter"
                            : "error.lighter",
                      }}
                    />
                  </TableCell>
                  {/*                   <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title="Editar Legajo">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(emp)}
                            sx={{ color: 'primary.main', '&:hover': { bgcolor: 'primary.lighter' } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={emp.activo === 1 ? "Dar de Baja" : "Activar"}>
                          <IconButton
                            size="small"
                            color={emp.activo === 1 ? "error" : "success"}
                            onClick={(e) => handleToggleActive(e, emp)}
                            sx={{ '&:hover': { bgcolor: emp.activo === 1 ? 'error.lighter' : 'success.lighter' } }}
                          >
                            <PersonOffIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell> */}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No hay empleados
                </TableCell>
              </TableRow>
            )}
            {/* Empty rows to maintain height if necessary */}
            {/*             {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6} />
              </TableRow>
            )} */}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={empleados?.pagination?.total || 0}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página"
      />

      <Dialog open={confirmDialog.open} onClose={handleCancelDeactivate}>
        {" "}
        <DialogTitle>
          {" "}
          {confirmDialog.employee?.activo === 1
            ? "Confirmar baja de empleado"
            : "Confirmar reactivación"}{" "}
        </DialogTitle>{" "}
        <DialogContent>
          {" "}
          <DialogContentText>
            ¿Está seguro que desea{" "}
            {confirmDialog.employee?.activo === 1
              ? "dar de baja a"
              : "reactivar a"}{" "}
            {confirmDialog.employee?.nombre} {confirmDialog.employee?.apellido}?{" "}
          </DialogContentText>{" "}
        </DialogContent>{" "}
        <DialogActions>
          <Button onClick={handleCancelDeactivate}>Cancelar</Button>{" "}
          <Button
            onClick={handleConfirmDeactivate} // 🚨 CAMBIO: Color dinámico según la acción
            color={confirmDialog.employee?.activo === 1 ? "error" : "success"}
            variant="contained"
          >
            {" "}
            {confirmDialog.employee?.activo === 1
              ? "Dar de baja"
              : "Reactivar"}{" "}
          </Button>{" "}
        </DialogActions>{" "}
      </Dialog>
    </Box>
  );
}
